import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, db, popupRedirectResolver } from '../services/firebase';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  RecaptchaVerifier,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  fetchSignInMethodsForEmail,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  OAuthProvider
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp, 
  collection, 
  addDoc,
  query,
  where,
  orderBy,
  limit as limitFirestore,
  getDocs
} from 'firebase/firestore';
import { incrementDailyUserCount } from '../services/adminService';
import { addDailyLoginReward, initializeUserWallets } from '../services/walletService';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isDemoMode] = useState(false); // Disabled demo mode
  
  // Demo user data - kept for reference but no longer used
  const demoUser = {
    uid: 'demo123',
    email: 'demo@gameprebattle.com',
    displayName: 'Demo User',
    isDemo: true,
    role: 'admin'
  };

  const signup = async (email, password) => {
    try {
      // Create the user with email and password
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Note: The user document will be created in the checkAndUpdateLoginBonus function
      // which is called by the onAuthStateChanged listener
      // This ensures user data is properly initialized
      
      return result;
    } catch (error) {
      console.error("Error during signup:", error);
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      console.log("Attempting login with email:", email);
      
      // Proceed directly with sign-in attempt
      try {
        const result = await signInWithEmailAndPassword(auth, email, password);
        await checkAndUpdateLoginBonus(result.user);
        return result; // Return full user credential
      } catch (signInError) {
        console.error("Sign-in error:", signInError.code, signInError.message);
        
        // Provide specific error messages based on Firebase error codes
        if (signInError.code === 'auth/invalid-email') {
          throw new Error('Invalid email format');
        } else if (signInError.code === 'auth/user-not-found') {
          throw new Error('No account exists with this email');
        } else if (signInError.code === 'auth/wrong-password') {
          throw new Error('Incorrect password');
        } else if (signInError.code === 'auth/invalid-credential') {
          throw new Error('Invalid login credentials. Please check your email and password.');
        } else if (signInError.code === 'auth/too-many-requests') {
          throw new Error('Too many failed attempts. Please try again later or reset your password.');
        } else if (signInError.code === 'auth/user-disabled') {
          throw new Error('This account has been disabled. Please contact support.');
        } else {
          throw signInError;
        }
      }
    } catch (error) {
      console.error("Login error:", error.code, error.message);
      throw error;
    }
  };

  const setupRecaptcha = (containerOrId) => {
    const recaptchaVerifier = new RecaptchaVerifier(auth, containerOrId, {
      size: 'invisible',
      callback: () => {}
    });
    return recaptchaVerifier;
  };

  // Generate a random 6-digit OTP
  const generateOTP = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const sendEmailVerification = async (email) => {
    try {
      // Generate a 6-digit OTP
      const otp = generateOTP();
      
      // Store OTP in Firestore with timestamp
      await addDoc(collection(db, 'otps'), {
        email,
        otp,
        createdAt: serverTimestamp(),
        used: false
      });
      
      // In a real implementation, you would send an email with the OTP
      // For this example, we'll just log it to the console
      console.log(`OTP for ${email}: ${otp}`);
      
      // You would integrate with an email service here to send the OTP
      
      return otp;
    } catch (error) {
      console.error("Error sending email OTP:", error);
      throw error;
    }
  };

  const verifyEmailOTP = async (email, otp) => {
    try {
      // Find the user with this email
      const userQuery = query(collection(db, 'users'), where('email', '==', email));
      const userSnapshot = await getDocs(userQuery);
      
      if (userSnapshot.empty) {
        throw new Error('No user found with this email');
      }
      
      // Check OTP in Firestore
      const otpQuery = query(
        collection(db, 'otps'),
        where('email', '==', email),
        where('otp', '==', otp),
        where('used', '==', false),
        orderBy('createdAt', 'desc'),
        limitFirestore(1)
      );
      const otpSnapshot = await getDocs(otpQuery);
      
      if (otpSnapshot.empty) {
        throw new Error('Invalid or expired OTP');
      }
      
      // Mark OTP as used
      const otpDoc = otpSnapshot.docs[0];
      await updateDoc(doc(db, 'otps', otpDoc.id), {
        used: true
      });
      
      // Set the current user
      const userData = userSnapshot.docs[0].data();
      const userId = userSnapshot.docs[0].id;
      
      // Update login timestamp
      await updateDoc(doc(db, 'users', userId), {
        lastLogin: serverTimestamp()
      });
      
      await checkAndUpdateLoginBonus({ uid: userId });
      
      // Return a user credential-like object with user property
      return { user: { uid: userId, email, ...userData } };
    } catch (error) {
      console.error("Error verifying email OTP:", error);
      throw error;
    }
  };

  const signOut = () => {
    return firebaseSignOut(auth);
  };

  const checkAndUpdateLoginBonus = async (user) => {
    if (!user) return;
    
    try {
      // Get user document from Firestore
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const lastLogin = userData.lastLogin ? new Date(userData.lastLogin.toDate()) : null;
        
        // Check if user is admin
        if (userData.role === 'admin') {
          setIsAdmin(true);
        }
        
        // If lastLogin exists and is from a different day, add bonus
        if (lastLogin && new Date(lastLogin.getFullYear(), lastLogin.getMonth(), lastLogin.getDate()).getTime() < today) {
          // Add daily login bonus with streak system
          const result = await addDailyLoginReward(user.uid);
          
          if (result && result.success) {
            // Show notification to user with the amount and streak info
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('login-reward', { 
                detail: { 
                  message: `You received ${result.amount} coins for your daily login! (Streak: ${result.streak})`,
                  streak: result.streak,
                  amount: result.amount 
                } 
              }));
              
              // Update localStorage with the last claim time
              if (window.localStorage) {
                localStorage.setItem(`lastClaim_${user.uid}`, new Date().getTime().toString());
              }
            }
          }
          
          // Increment daily users count
          await incrementDailyUserCount();
        }
        
        // Update lastLogin timestamp
        await updateDoc(userRef, {
          lastLogin: serverTimestamp()
        });
      } else {
        // Create user document if it doesn't exist
        await setDoc(userRef, {
          email: user.email,
          displayName: user.displayName || user.email.split('@')[0],
          role: 'user', // Default role
          lastLogin: serverTimestamp(),
          createdAt: serverTimestamp()
        });
        
        // Increment daily users count for new users
        await incrementDailyUserCount();
      }
    } catch (error) {
      console.error("Error checking/updating login bonus:", error);
    }
  };

  const sendEmailSignInLink = async (email) => {
    try {
      console.log("Sending email sign-in link to:", email);
      
      // Use the production domain
      const baseUrl = "https://gameprebattle.com";
      
      // Create a proper URL for the redirect
      const redirectUrl = `${baseUrl}/finishSignIn`;
      console.log("Redirect URL for email link:", redirectUrl);
      
      // ActionCodeSettings defines the URL to redirect to after clicking the link
      const actionCodeSettings = {
        // URL you want to redirect back to after sign-in
        url: redirectUrl,
        // This must be true for email link sign-in
        handleCodeInApp: true,
        // Set the dynamic link domain if you're using Firebase Dynamic Links (optional)
        dynamicLinkDomain: "gameprebattle.com",
        // Set the iOS bundle ID if you have an iOS app (optional)
        // iOS: {
        //   bundleId: 'com.example.ios'
        // },
        // Set the Android package name if you have an Android app (optional)
        // android: {
        //   packageName: 'com.example.android',
        //   installApp: true,
        //   minimumVersion: '12'
        // }
      };
      
      console.log("Action code settings:", actionCodeSettings);
      
      try {
        await sendSignInLinkToEmail(auth, email, actionCodeSettings);
        console.log("Email link sent successfully");
        
        // Save the email in localStorage to use it later when the user clicks the link
        window.localStorage.setItem('emailForSignIn', email);
        
        return { success: true };
      } catch (sendError) {
        console.error("Error sending sign-in link:", sendError);
        throw sendError;
      }
    } catch (error) {
      console.error("Error in sendEmailSignInLink:", error);
      throw error;
    }
  };

  const confirmSignInWithEmailLink = async (email, link) => {
    try {
      console.log("Starting confirmSignInWithEmailLink with email:", email);
      
      if (!email) {
        console.error("Email is missing");
        throw new Error('Email is required to complete sign-in');
      }
      
      if (!link) {
        console.error("Link is missing");
        throw new Error('Invalid sign-in link');
      }
      
      if (!isSignInWithEmailLink(auth, link)) {
        console.error("Not a valid sign-in link");
        throw new Error('Not a valid sign-in link');
      }
      
      console.log("Attempting signInWithEmailLink with:", { email, link });
      
      // Sign in the user with the email and the link
      try {
        const result = await signInWithEmailLink(auth, email, link);
        console.log("signInWithEmailLink success:", result);
        
        // If this is a new user, create their profile in Firestore
        if (result.user.metadata.creationTime === result.user.metadata.lastSignInTime) {
          console.log("New user detected, creating profile");
          // Create user document if it doesn't exist
          const userRef = doc(db, 'users', result.user.uid);
          await setDoc(userRef, {
            email: result.user.email,
            displayName: result.user.email.split('@')[0],
            role: 'user',
            lastLogin: serverTimestamp(),
            createdAt: serverTimestamp()
          }, { merge: true });
          
          // Initialize wallet for new user
          await initializeUserWallets(result.user.uid);
        } else {
          console.log("Existing user, updating login timestamp");
          // Update login timestamp and check for login bonus
          await checkAndUpdateLoginBonus(result.user);
        }
        
        // Clear the email from localStorage
        window.localStorage.removeItem('emailForSignIn');
        
        return result;
      } catch (signInError) {
        console.error("Error in signInWithEmailLink:", signInError);
        throw signInError;
      }
    } catch (error) {
      console.error("Error confirming sign-in with email link:", error);
      throw error;
    }
  };

  const checkIfEmailLink = (link) => {
    return isSignInWithEmailLink(auth, link);
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      console.error("Error sending password reset email:", error);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      // Add prompt to select account to prevent silent sign-in with cached accounts
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      const result = await signInWithPopup(auth, provider, popupRedirectResolver);
      
      // Check if this is a new user by checking if their document exists in Firestore
      const userRef = doc(db, 'users', result.user.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        // This is a completely new user, create their document
        await setDoc(userRef, {
          email: result.user.email,
          displayName: result.user.displayName || result.user.email.split('@')[0],
          photoURL: result.user.photoURL,
          role: 'user',
          lastLogin: serverTimestamp(),
          createdAt: serverTimestamp(),
          provider: 'google',
          walletA: 0,
          walletB: 0,
          ads: 0,
          referrals: 0,
          login: 0,
          accuracyBonus: 0
        });
        
        // Initialize wallets for new user
        await initializeUserWallets(result.user.uid);
      } else {
        // Existing user, make sure we preserve their wallet data
        const userData = userDoc.data();
        
        // Update user information while preserving wallet data
        await updateDoc(userRef, {
          email: result.user.email,
          displayName: result.user.displayName || userData.displayName || result.user.email.split('@')[0],
          photoURL: result.user.photoURL || userData.photoURL,
          lastLogin: serverTimestamp(),
          provider: 'google'
        });
        
        // Update login timestamp and check for login bonus
        await checkAndUpdateLoginBonus(result.user);
      }
      
      return result;
    } catch (error) {
      console.error("Google sign-in error:", error);
      throw error;
    }
  };

  const signInWithApple = async () => {
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      
      const result = await signInWithPopup(auth, provider, popupRedirectResolver);
      
      // Check if this is a new user by checking if their document exists in Firestore
      const userRef = doc(db, 'users', result.user.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        // This is a completely new user, create their document
        await setDoc(userRef, {
          email: result.user.email,
          displayName: result.user.displayName || result.user.email.split('@')[0],
          photoURL: result.user.photoURL,
          role: 'user',
          lastLogin: serverTimestamp(),
          createdAt: serverTimestamp(),
          provider: 'apple',
          walletA: 0,
          walletB: 0,
          ads: 0,
          referrals: 0,
          login: 0,
          accuracyBonus: 0
        });
        
        // Initialize wallets for new user
        await initializeUserWallets(result.user.uid);
      } else {
        // Existing user, make sure we preserve their wallet data
        const userData = userDoc.data();
        
        // Update user information while preserving wallet data
        await updateDoc(userRef, {
          email: result.user.email,
          displayName: result.user.displayName || userData.displayName || result.user.email.split('@')[0],
          photoURL: result.user.photoURL || userData.photoURL,
          lastLogin: serverTimestamp(),
          provider: 'apple'
        });
        
        // Update login timestamp and check for login bonus
        await checkAndUpdateLoginBonus(result.user);
      }
      
      return result;
    } catch (error) {
      console.error("Apple sign-in error:", error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          // Check if user is admin
          const userRef = doc(db, 'users', user.uid);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            setIsAdmin(userSnap.data().role === 'admin');
          }
        } catch (error) {
          console.error("Error checking admin status:", error);
        }
      } else {
        setIsAdmin(false);
      }
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    currentUser,
    isAdmin,
    signup,
    login,
    signOut,
    setupRecaptcha,
    sendEmailVerification,
    verifyEmailOTP,
    sendEmailSignInLink,
    confirmSignInWithEmailLink,
    checkIfEmailLink,
    resetPassword,
    isDemoMode,
    signInWithGoogle,
    signInWithApple
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 